import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

type UserBudgetGroup = components['schemas']['UserBudgetGroup'];

type BudgetSnapshotRequest = components['schemas']['AddBudgetGroupsToUserEndpoint_BudgetSnapshotRequest'];

type BudgetsAutocompleteResponse = components['schemas']['GetBudgetsAutocompleteEndpoint_BudgetsAutocompleteResponse'];

export type EnhancedBudgetGroup = BudgetsAutocompleteResponse & {
  isAssignedToUser: boolean;
  role?: string;
};

export interface UseManageUserBudgetGroupsReturn {
  addGroups: (userId: string, groupNames: EnhancedBudgetGroup[]) => Promise<UserBudgetGroup[] | null>;
  removeGroup: (groupId: number, userId: string, role: string) => Promise<boolean>;
  removeGroups: (userId: string, groupRoles: { groupId: number; role: string }[]) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  removeGroupMutation: UseMutationResult<
    boolean,
    unknown,
    {
      groupId: number;
      userId: string;
      role: string;
    },
    unknown
  >;
  removeGroupsMutation: UseMutationResult<
    boolean,
    unknown,
    {
      userId: string;
      groupRoles: { groupId: number; role: string }[];
    },
    unknown
  >;
}

interface UseManageUserBudgetGroupsConfig {
  snackbar: SnackbarContextType;
}

export function useManageUserBudgetGroups({ snackbar }: UseManageUserBudgetGroupsConfig): UseManageUserBudgetGroupsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const getBudgetGroupInfo = useCallback(
    async (groupName: EnhancedBudgetGroup): Promise<BudgetSnapshotRequest | null> => {
      try {
        const client = createApiClient(getAccessToken);
        const response = await client.GET('/api/organizations/{organizationId}/budget-groups/autocomplete', {
          params: {
            path: { organizationId },
            query: { Filter: groupName.name ?? '' },
          },
        });
        const data = response.data as BudgetsAutocompleteResponse[] | undefined;

        if (!data || data.length === 0) {
          return null;
        }

        const group = data.find((g) => (g as BudgetsAutocompleteResponse).name === groupName.name);

        if (!group) {
          return null;
        }

        const budgetGroup = group as BudgetsAutocompleteResponse;
        if (!budgetGroup.id || !budgetGroup.name) {
          return null;
        }

        return {
          budgetGroupId: budgetGroup.id,
          budgetName: budgetGroup.name,
          isActive: budgetGroup.isActive ?? true,
        } as BudgetSnapshotRequest;
      } catch (err: unknown) {
        console.error('Error fetching budget group info:', err);
        return null;
      }
    },
    [organizationId, getAccessToken],
  );

  const addGroupsMutation = useMutation({
    mutationFn: async ({ userId, groupNames }: { userId: string; groupNames: EnhancedBudgetGroup[] }): Promise<UserBudgetGroup[]> => {
      const groupSnapshots: BudgetSnapshotRequest[] = [];
      for (const groupName of groupNames) {
        const groupInfo = await getBudgetGroupInfo(groupName);
        if (groupInfo) {
          groupSnapshots.push(groupInfo);
        }
      }

      if (groupSnapshots.length === 0) {
        throw new Error('No valid budget groups found');
      }

      const client = createApiClient(getAccessToken);

      const requestBody = {
        userId,
        budgets: groupSnapshots,
      };
      const result = await client.POST('/api/organizations/{organizationId}/users/budget-groups', {
        params: { path: { organizationId } },
        body: requestBody,
      });
      return Array.isArray(result.data) ? (result.data as UserBudgetGroup[]) : [];
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-budget-group-membership', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['budgetGroups', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addGroups = useCallback(
    async (userId: string, groupNames: EnhancedBudgetGroup[]): Promise<UserBudgetGroup[] | null> => {
      try {
        const result = await addGroupsMutation.mutateAsync({ userId, groupNames });
        return result;
      } catch {
        return null;
      }
    },
    [addGroupsMutation],
  );

  const removeGroupMutation = useMutation({
    mutationFn: async ({ groupId, userId, role }: { groupId: number; userId: string; role: string }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const { response } = await client.DELETE('/api/organizations/{organizationId}/budget-groups/{budgetGroupId}/users', {
        params: {
          path: { organizationId, budgetGroupId: groupId },
          query: { UserId: userId, Role: role },
        },
      });
      return response.status === 204 || response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-budget-group-membership', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeGroup = async (groupId: number, userId: string, role: string): Promise<boolean> => {
    try {
      return await removeGroupMutation.mutateAsync({ groupId, userId, role });
    } catch {
      return false;
    }
  };

  const removeGroupsMutation = useMutation({
    mutationFn: async ({ userId, groupRoles }: { userId: string; groupRoles: { groupId: number; role: string }[] }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const { response } = await client.DELETE('/api/organizations/{organizationId}/users/budget-groups/batch', {
        params: {
          path: { organizationId },
        },
        body: {
          userId,
          budgetGroups: groupRoles.map(({ groupId, role }) => ({ budgetGroupId: groupId, role })),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove budget groups. Request failed with status ${response.status}.`);
      }

      return true;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['user-budget-group-membership', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['budget-groups-autocomplete', organizationId],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeGroups = async (userId: string, groupRoles: { groupId: number; role: string }[]): Promise<boolean> => {
    try {
      return await removeGroupsMutation.mutateAsync({ userId, groupRoles });
    } catch {
      return false;
    }
  };

  const loading = addGroupsMutation.isPending || removeGroupMutation.isPending || removeGroupsMutation.isPending;

  const error =
    addGroupsMutation.error instanceof Error
      ? addGroupsMutation.error.message
      : removeGroupMutation.error instanceof Error
        ? removeGroupMutation.error.message
        : removeGroupsMutation.error instanceof Error
          ? removeGroupsMutation.error.message
          : null;

  return {
    addGroups,
    removeGroup,
    removeGroups,
    loading,
    error,
    removeGroupMutation,
    removeGroupsMutation,
  };
}
